import { AgentIcon } from "../Dimension.jsx";
import { agentDisplayLabel } from "../../lib/agentIdentity.js";

/**
 * Detail-drawer list of an endpoint's installed AI agents. Unlike the
 * inventory-table cell — which shows only the latest version + a `*` and reveals
 * the rest on hover — the drawer isn't hover-gated, so a multi-version tool
 * lists every installed version inline (still marked with `*`). Consumes the
 * deduped `installedAiAgentsList` entries (`{ name, version, versions }`) from
 * `flattenInventoryRow`. An entry also carries `distinguisher` (its install path)
 * when another entry in the same list renders the same label. Without it, two
 * different tools reported only by a coarse provider tag are two identical lines.
 */
export default function InstalledAgentsDetail({ agents }) {
    return (
        <ul className="grid gap-[4px]">
            {agents.map((a, i) => {
                const multi = (a.versions?.length ?? 0) > 1;
                return (
                    <li key={i} className="text-[12px] text-[color:var(--ink)]">
                        <div className="flex items-center gap-[6px]">
                            <AgentIcon name={a.name} className="inline-block h-3 w-3 shrink-0" />
                            <span>{agentDisplayLabel(a.name)}</span>
                            {/* Single-version tools show the version inline; multi-version
                                tools show only `*` here and list every version below, so the
                                latest isn't printed twice. */}
                            {!multi && a.version ? (
                                <span className="text-[color:var(--muted-soft)]">{a.version}</span>
                            ) : null}
                            {multi ? <span className="text-[color:var(--ink)]">*</span> : null}
                        </div>
                        {/* Shown only when another entry renders the same label; the path
                            is the thing that actually tells them apart. */}
                        {a.distinguisher ? (
                            <div className="pl-[22px] text-[11px] leading-[1.3] text-[color:var(--muted-soft)] break-all">
                                {a.distinguisher}
                            </div>
                        ) : null}
                        {/* Detail panel isn't hover-gated: list every installed version inline. */}
                        {multi ? (
                            <div className="grid gap-[1px] pl-[22px] pt-[1px]">
                                {a.versions.map((v) => (
                                    <span
                                        key={v}
                                        className="text-[11px] leading-[1.3] text-[color:var(--muted-soft)]"
                                    >
                                        {v}
                                    </span>
                                ))}
                            </div>
                        ) : null}
                    </li>
                );
            })}
        </ul>
    );
}
